import { c as _c } from "react/compiler-runtime";
import { type TableOptions, useReactTable } from '@tanstack/react-table';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { CHART_CARD_TABLE_LAYOUT } from '../../constants';
import type { ChartDataRow } from '../../types';
import { svgToPng, type SvgToPngOptions } from '../../utils/graph';
import type { ChartCardTable, ChartCardTableOptions } from './tanstack';
export const useHovered = () => {
  const $ = _c(4);
  const [hovered, setHovered] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setHovered(true);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onMouseEnter = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHovered(false);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onMouseLeave = t1;
  let t2;
  if ($[2] !== hovered) {
    t2 = {
      hovered,
      onMouseEnter,
      onMouseLeave
    };
    $[2] = hovered;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

/**
 * Rasterizes SVG markup or an {@link SVGSVGElement} to a PNG and returns a `blob:` object URL.
 * The URL is revoked when `svg` / options change or the host component unmounts.
 */
export const useSvgToPngObjectUrl = (svg, options) => {
  const $ = _c(8);
  const [objectUrl, setObjectUrl] = useState(null);
  let t0;
  if ($[0] !== options) {
    t0 = options ?? {};
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    width,
    height,
    pixelRatio
  } = t0;
  let t1;
  let t2;
  if ($[2] !== height || $[3] !== pixelRatio || $[4] !== svg || $[5] !== width) {
    t1 = () => {
      if (svg == null || typeof svg === "string" && svg.trim() === "") {
        setObjectUrl(_temp);
        return;
      }
      let cancelled = false;
      const rasterOptions = width != null || height != null || pixelRatio != null ? {
        width,
        height,
        pixelRatio
      } : undefined;
      (async () => {
        const blob = await svgToPng(svg, rasterOptions);
        if (cancelled) {
          return;
        }
        if (blob == null) {
          setObjectUrl(_temp2);
          return;
        }
        const nextUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        setObjectUrl(previous_1 => {
          if (previous_1 != null) {
            URL.revokeObjectURL(previous_1);
          }
          return nextUrl;
        });
      })();
      return () => {
        cancelled = true;
        setObjectUrl(_temp3);
      };
    };
    t2 = [svg, width, height, pixelRatio];
    $[2] = height;
    $[3] = pixelRatio;
    $[4] = svg;
    $[5] = width;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  return objectUrl;
};
export const useChartCardTable = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(options: ChartCardTableOptions<DataKey, DataRow>): ChartCardTable<DataKey, DataRow> => {
  return useReactTable<DataRow>(options as TableOptions<DataRow>) as ChartCardTable<DataKey, DataRow>;
};

/**
 * Tracks an element's `clientWidth` via a callback ref + ResizeObserver.
 * Use `setRef` on the DOM node and `ref` wherever a stable RefObject is needed.
 */
export const useElementClientWidth = () => {
  const $ = _c(6);
  const ref = useRef(null);
  const [element, setElement] = useState(null);
  const [clientWidth, setClientWidth] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      ref.current = node;
      setElement(node);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const setRef = t0;
  let t1;
  let t2;
  if ($[1] !== element) {
    t1 = () => {
      if (element == null) {
        setClientWidth(0);
        return;
      }
      const updateWidth = () => {
        setClientWidth(element.clientWidth);
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(element);
      return () => {
        resizeObserver.disconnect();
      };
    };
    t2 = [element];
    $[1] = element;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[4] !== clientWidth) {
    t3 = {
      setRef,
      ref,
      clientWidth
    };
    $[4] = clientWidth;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
interface UseTableBodyScrollDecorationParams {
  enabled: boolean;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  tableHeadRootRef: RefObject<HTMLTableSectionElement | null>;
  tableFooterRootRef: RefObject<HTMLTableSectionElement | null>;
  processedData: unknown;
  globalFilter: string;
  containerHeight: number | `${number}%`;
  containerWidth: number | `${number}%`;
  pageIndex: number | undefined;
  pageSize: number | undefined;
}

/**
 * Measures header/footer height and whether the table body can scroll further
 * up/down, for the top/bottom fade decorations.
 */
export const useTableBodyScrollDecoration = t0 => {
  const $ = _c(21);
  const {
    enabled,
    tableContainerRef,
    tableHeadRootRef,
    tableFooterRootRef,
    processedData,
    globalFilter,
    containerHeight,
    containerWidth,
    pageIndex,
    pageSize
  } = t0;
  const [showBottomScrollFade, setShowBottomScrollFade] = useState(false);
  const [showTopScrollFade, setShowTopScrollFade] = useState(false);
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState(0);
  const [measuredFooterHeight, setMeasuredFooterHeight] = useState(0);
  let t1;
  if ($[0] !== enabled || $[1] !== tableContainerRef || $[2] !== tableFooterRootRef || $[3] !== tableHeadRootRef) {
    t1 = () => {
      if (tableContainerRef.current == null || !enabled) {
        setShowBottomScrollFade(false);
        setShowTopScrollFade(false);
        setMeasuredHeaderHeight(0);
        setMeasuredFooterHeight(0);
        return;
      }
      const scrollElement = tableContainerRef.current;
      const measure = function measure() {
        resizeObserver.observe(scrollElement);
        const headerEl = tableHeadRootRef.current;
        let headerH = 0;
        if (headerEl != null) {
          resizeObserver.observe(headerEl);
          headerH = Math.round(headerEl.getBoundingClientRect().height);
        }
        setMeasuredHeaderHeight(prev => prev === headerH ? prev : headerH);
        const footerEl = tableFooterRootRef.current;
        let footerH = 0;
        if (footerEl != null) {
          resizeObserver.observe(footerEl);
          footerH = Math.round(footerEl.getBoundingClientRect().height);
        }
        setMeasuredFooterHeight(prev_0 => prev_0 === footerH ? prev_0 : footerH);
        const {
          scrollTop,
          clientHeight,
          scrollHeight
        } = scrollElement;
        const hasMoreBelow = scrollTop + clientHeight < scrollHeight - (CHART_CARD_TABLE_LAYOUT.padding.body?.bottom ?? 2);
        const hasMoreAbove = scrollTop > (CHART_CARD_TABLE_LAYOUT.padding.body?.top ?? 2);
        setShowBottomScrollFade(hasMoreBelow);
        setShowTopScrollFade(hasMoreAbove);
      };
      const resizeObserver = new ResizeObserver(measure);
      measure();
      const raf = requestAnimationFrame(measure);
      scrollElement.addEventListener("scroll", measure, {
        passive: true
      });
      return () => {
        cancelAnimationFrame(raf);
        scrollElement.removeEventListener("scroll", measure);
        resizeObserver.disconnect();
      };
    };
    $[0] = enabled;
    $[1] = tableContainerRef;
    $[2] = tableFooterRootRef;
    $[3] = tableHeadRootRef;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== containerHeight || $[6] !== containerWidth || $[7] !== enabled || $[8] !== globalFilter || $[9] !== pageIndex || $[10] !== pageSize || $[11] !== processedData || $[12] !== tableContainerRef || $[13] !== tableFooterRootRef || $[14] !== tableHeadRootRef) {
    t2 = [enabled, tableContainerRef, tableHeadRootRef, tableFooterRootRef, processedData, globalFilter, containerHeight, containerWidth, pageIndex, pageSize];
    $[5] = containerHeight;
    $[6] = containerWidth;
    $[7] = enabled;
    $[8] = globalFilter;
    $[9] = pageIndex;
    $[10] = pageSize;
    $[11] = processedData;
    $[12] = tableContainerRef;
    $[13] = tableFooterRootRef;
    $[14] = tableHeadRootRef;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[16] !== measuredFooterHeight || $[17] !== measuredHeaderHeight || $[18] !== showBottomScrollFade || $[19] !== showTopScrollFade) {
    t3 = {
      showTopScrollFade,
      showBottomScrollFade,
      measuredHeaderHeight,
      measuredFooterHeight
    };
    $[16] = measuredFooterHeight;
    $[17] = measuredHeaderHeight;
    $[18] = showBottomScrollFade;
    $[19] = showTopScrollFade;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
};
function _temp(previous) {
  if (previous != null) {
    URL.revokeObjectURL(previous);
  }
  return null;
}
function _temp2(previous_0) {
  if (previous_0 != null) {
    URL.revokeObjectURL(previous_0);
  }
  return null;
}
function _temp3(previous_2) {
  if (previous_2 != null) {
    URL.revokeObjectURL(previous_2);
  }
  return null;
}